import React from 'react';
import { ExternalLink } from 'lucide-react';

const ExternalLinksPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-slate-800 mb-8">External Links</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-lg text-gray-700 mb-8">
            Below are useful external resources and links that complement our services and provide 
            additional support for mental health and wellbeing.
          </p>

          {/* Mental Health Organizations */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6 flex items-center">
              <ExternalLink className="w-6 h-6 mr-2" />
              Mental Health Organizations
            </h2>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Mind</h3>
                <p className="text-gray-700 mb-2">Mental health charity providing advice and support</p>
                <a href="https://www.mind.org.uk" target="_blank" rel="noopener noreferrer" 
                   className="text-blue-600 hover:text-blue-800 underline">
                  www.mind.org.uk
                </a>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Samaritans</h3>
                <p className="text-gray-700 mb-2">24/7 emotional support for anyone in distress</p>
                <a href="https://www.samaritans.org" target="_blank" rel="noopener noreferrer" 
                   className="text-green-600 hover:text-green-800 underline">
                  www.samaritans.org
                </a>
                <p className="text-sm text-gray-600 mt-1">Call: 116 123 (free, 24/7)</p>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Rethink Mental Illness</h3>
                <p className="text-gray-700 mb-2">Support and information for mental illness</p>
                <a href="https://www.rethink.org" target="_blank" rel="noopener noreferrer" 
                   className="text-purple-600 hover:text-purple-800 underline">
                  www.rethink.org
                </a>
              </div>
            </div>
          </section>

          {/* NHS Resources */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6">NHS Resources</h2>
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">NHS Mental Health Services</h3>
                <p className="text-gray-700 mb-2">Information about NHS mental health support</p>
                <a href="https://www.nhs.uk/mental-health" target="_blank" rel="noopener noreferrer" 
                   className="text-red-600 hover:text-red-800 underline">
                  www.nhs.uk/mental-health
                </a>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">NHS 111</h3>
                <p className="text-gray-700 mb-2">Non-emergency medical help and advice</p>
                <a href="https://www.nhs.uk/using-the-nhs/nhs-services/urgent-and-emergency-care/nhs-111" 
                   target="_blank" rel="noopener noreferrer" 
                   className="text-blue-600 hover:text-blue-800 underline">
                  NHS 111 Online
                </a>
                <p className="text-sm text-gray-600 mt-1">Call: 111 (free, 24/7)</p>
              </div>
            </div>
          </section>

          {/* Crisis Support */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6">Crisis Support</h2>
            <div className="bg-red-50 border-l-4 border-red-500 p-6">
              <h3 className="text-lg font-semibold text-red-800 mb-4">Emergency Contacts</h3>
              <div className="space-y-2">
                <p><strong>Emergency Services:</strong> 999</p>
                <p><strong>Samaritans:</strong> 116 123 (free, 24/7)</p>
                <p><strong>Crisis Text Line:</strong> Text SHOUT to 85258</p>
                <p><strong>NHS 111:</strong> 111 (non-emergency)</p>
              </div>
            </div>
          </section>

          {/* Professional Bodies */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6">Professional Bodies</h2>
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">British Psychological Society</h3>
                <p className="text-gray-700 mb-2">Professional body for psychologists in the UK</p>
                <a href="https://www.bps.org.uk" target="_blank" rel="noopener noreferrer" 
                   className="text-gray-600 hover:text-gray-800 underline">
                  www.bps.org.uk
                </a>
              </div>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">British Association for Counselling and Psychotherapy</h3>
                <p className="text-gray-700 mb-2">Professional body for counsellors and psychotherapists</p>
                <a href="https://www.bacp.co.uk" target="_blank" rel="noopener noreferrer" 
                   className="text-gray-600 hover:text-gray-800 underline">
                  www.bacp.co.uk
                </a>
              </div>
            </div>
          </section>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-8">
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Disclaimer</h3>
            <p className="text-gray-700 text-sm">
              These external links are provided for information purposes only. HealthCity does not endorse 
              or take responsibility for the content of external websites. Please use your discretion when 
              accessing external resources. If you are experiencing a mental health crisis, please contact 
              emergency services or the crisis support numbers listed above.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExternalLinksPage;